import { useEffect, useState } from "react";

/**
 * Hook PWA install — expose si l'app peut etre installee comme app
 * native depuis le browser, et la fonction pour declencher le prompt.
 *
 * Modes :
 *   - "native"            : Android Chrome / Edge / Brave avec beforeinstallprompt
 *                           dispo. promptInstall() declenche le dialog natif.
 *   - "ios-manual"        : iOS Safari — pas d'event natif possible, l'install
 *                           se fait via Share > "Sur l'ecran d'accueil". Le caller
 *                           doit afficher un tuto guide a l'user.
 *   - "already-installed" : l'app tourne deja en standalone mode (depuis l'icone
 *                           home screen) → bouton install masque.
 *   - "in-tauri"          : on est dans la webview Tauri → bouton install masque.
 *   - "unsupported"       : browser desktop, ou mobile sans installability →
 *                           caller affiche "Telecharger" desktop binary a la place.
 *
 * Le `beforeinstallprompt` event fire UNE SEULE FOIS au page load, souvent
 * AVANT que React mount. On le capture au niveau module (cachedPromptEvent)
 * pour que les consumers puissent prompt n'importe quand.
 */

interface BeforeInstallPromptEvent extends Event {
  prompt: () => Promise<void>;
  userChoice: Promise<{ outcome: "accepted" | "dismissed" }>;
}

const IS_IN_TAURI =
  typeof window !== "undefined" && "__TAURI_INTERNALS__" in window;

const IS_IOS_SAFARI: boolean = (() => {
  if (typeof navigator === "undefined") return false;
  const ua = navigator.userAgent;
  const isIOSDevice = /iPad|iPhone|iPod/.test(ua);
  if (!isIOSDevice) return false;
  // Exclure Chrome iOS (CriOS) / Firefox iOS (FxiOS) — meme sur iOS, ils ne
  // peuvent pas installer (Apple limite l'API aux WebView Safari natifs).
  return /Safari/.test(ua) && !/CriOS|FxiOS|EdgiOS|OPiOS/.test(ua);
})();

const IS_STANDALONE: boolean = (() => {
  if (typeof window === "undefined") return false;
  if (window.matchMedia("(display-mode: standalone)").matches) return true;
  // iOS Safari expose `navigator.standalone` (proprietary)
  const navWithStandalone = window.navigator as Navigator & {
    standalone?: boolean;
  };
  return navWithStandalone.standalone === true;
})();

// Capture au niveau module — l'event fire avant React mount.
let cachedPromptEvent: BeforeInstallPromptEvent | null = null;
const promptListeners = new Set<
  (e: BeforeInstallPromptEvent | null) => void
>();

if (typeof window !== "undefined" && !IS_IN_TAURI) {
  window.addEventListener("beforeinstallprompt", (e) => {
    e.preventDefault();
    cachedPromptEvent = e as BeforeInstallPromptEvent;
    promptListeners.forEach((l) => l(cachedPromptEvent));
  });
  window.addEventListener("appinstalled", () => {
    cachedPromptEvent = null;
    promptListeners.forEach((l) => l(null));
  });
}

export type InstallSupport =
  | "native"
  | "ios-manual"
  | "unsupported"
  | "already-installed"
  | "in-tauri";

export interface UseInstallPromptResult {
  support: InstallSupport;
  /** True si le prompt natif est dispo IMMEDIATEMENT (Android Chrome only). */
  canPromptNow: boolean;
  /** Declenche le prompt natif. Retourne "not-supported" si pas dispo. */
  promptInstall: () => Promise<"accepted" | "dismissed" | "not-supported">;
}

export function useInstallPrompt(): UseInstallPromptResult {
  const [promptEvent, setPromptEvent] = useState<BeforeInstallPromptEvent | null>(
    cachedPromptEvent,
  );

  useEffect(() => {
    promptListeners.add(setPromptEvent);
    return () => {
      promptListeners.delete(setPromptEvent);
    };
  }, []);

  let support: InstallSupport;
  if (IS_IN_TAURI) {
    support = "in-tauri";
  } else if (IS_STANDALONE) {
    support = "already-installed";
  } else if (promptEvent) {
    support = "native";
  } else if (IS_IOS_SAFARI) {
    support = "ios-manual";
  } else {
    support = "unsupported";
  }

  const promptInstall = async (): Promise<
    "accepted" | "dismissed" | "not-supported"
  > => {
    if (!promptEvent) return "not-supported";
    await promptEvent.prompt();
    const choice = await promptEvent.userChoice;
    return choice.outcome;
  };

  return {
    support,
    canPromptNow: !!promptEvent,
    promptInstall,
  };
}
